<template>
    <div>
        <ColumnsSelector
            ref="received-requests-columns"
            :fields="fields"
            :active-fields="activeFields"
            @activate="activateColumn"
            @inactivate="inactivateColumn"
        />
        <ForwardedRequestsModal
            ref="activeSentRequests"
        />
        <BAlert
            v-if="bulkReplyCandidatesError"
            show
            variant="danger"
        >
            <div class="alert-body d-flex align-items-center justify-content-between">
                <span>{{ $t('Page(ReceivedRequests):Component(BulkReply):load-error') }}</span>
                <BButton
                    size="sm"
                    variant="outline-danger"
                    @click="retryBulkReplyCandidates"
                >
                    {{ $t('Component(Button):retry') }}
                </BButton>
            </div>
        </BAlert>
        <BAlert
            v-else-if="bulkReplyCandidatesLoaded && bulkReplyCandidateIds.length === 0"
            show
            variant="secondary"
        >
            <div class="alert-body">
                {{ $t('Page(ReceivedRequests):Component(BulkReply):no-compatible-requests') }}
            </div>
        </BAlert>
        <HamletTable
            ref="hamletTable"
            :columns="tableFields"
            :fetch="fetch"
            :search="isSearchActive"
            :busy="bulkReplyCandidatesLoading"
            :default-per-page="savedPerPage"
            :default-page="savedPage"
            :default-filters="getDefaultFilters()"
            table-container-class="table-responsive hamlet-table received-requests-table"
            @onRowClick="onRowClick"
            @per-page-change="onPerPageChange"
            @page-change="onPageChange"
        >
            <template #header>
                <div class="card-header received-requests-header">
                    <div class="received-requests-header__start">
                        <BButton
                            variant="primary"
                            :disabled="!canStartBulkReply"
                            @click="startBulkReply"
                        >
                            {{ $t('Page(ReceivedRequests):Component(BulkReply):action', { count: selectedBulkReplyRequestIds.length }) }}
                        </BButton>
                    </div>

                    <h4 class="mb-0 text-center">
                        {{ $t('Page(ReceivedRequests):Component(Label):page-label') }}
                    </h4>

                    <div class="received-requests-header__end">
                        <BButton
                            v-if="primaryBulkReplyRequest"
                            class="mr-1"
                            variant="outline-secondary"
                            @click="clearBulkReplySelection"
                        >
                            {{ $t('Component(Button):clear-selection') }}
                        </BButton>
                        <BButton
                            variant="primary"
                            class="btn-icon"
                            @click="showColumns"
                        >
                            <feather-icon icon="ColumnsIcon" />
                        </BButton>
                    </div>
                </div>
            </template>

            <template #header-cell-selected>
                <BFormCheckbox
                    :checked="areAllBulkReplyCandidatesSelected"
                    :indeterminate="isBulkReplyCandidateSelectionPartial"
                    :disabled="!canToggleAllBulkReplyCandidates"
                    @change="toggleAllBulkReplyCandidates"
                    @click.native.stop
                />
            </template>

            <template #cell-selected="{ item }">
                <BFormCheckbox
                    v-if="isBulkReplyRequestAvailable(item)"
                    :checked="isBulkReplyRequestSelected(item.id)"
                    :disabled="!isBulkReplyRequestSelectable(item)"
                    @change="checked => toggleBulkReplyRequest(item, checked)"
                    @click.native.stop
                />
            </template>

            <template #cell-messagingStatus="data">
                <div class="text-center">
                    <span v-if="data.item.messagingStatus !== 'none'">
                        <BIconEnvelope v-if="data.item.messagingStatus === 'sent'" scale="1.5" />
                        <BIconEnvelopeOpen v-else scale="1.5" />
                    </span>
<!--                    <b-badge-->
<!--                        v-if="data.item.messagingStatus !== 'none'"-->
<!--                        :title="data.item.messagingStatus === 'sent' ? '' : $t('Page(ReceivedRequests):Component(Grid):hover-has-unseen-messages')"-->
<!--                        :variant="data.item.messagingStatus === 'sent' ? 'success' : 'danger'"-->
<!--                    >-->
<!--                        <feather-icon icon="SendIcon" />-->
<!--                    </b-badge>-->
                </div>
            </template>

            <template #cell-sentAt="data">
                <div style="min-width: 86px;">{{ renderDate(data.item.sentAt) }}</div>
            </template>

            <template #cell-deadlineDays="data">
                <div class="text-center">{{ data.item.daysToDeadLine }}</div>
            </template>

            <template #cell-level="data">
                <div :title="$t('Page(ReceivedRequests):Component(Grid):hover-field-level')">
                    {{ $t('Component(InformationLevelEnum):' + data.item.informationLevel) }}
                </div>
            </template>

            <template #cell-verification="data">
                <div :title="$t('Page(ReceivedRequests):Component(Grid):hover-field-verification')">
                    <b-badge v-for="document in data.item.verificationDocuments" class="badge-glow verification-badge">
                        {{ $t('Component(VerificationDocumentTypeEnum):' + document) }}
                    </b-badge>
                </div>
            </template>

            <template #filter-cell-productId>
                <FilterInput
                    style="min-width: 120px;"
                    :initial-value="filters.productId"
                    @search="filterByProductId"
                />
            </template>

            <template #cell-productId="data">
                <div :title="$t('Page(ReceivedRequests):Component(Grid):hover-field-product-id')">
                    {{ getProductId(data.item) }}
                </div>
            </template>

            <template #filter-cell-productName>
                <FilterInput
                    style="min-width: 120px;"
                    :initial-value="filters.productName"
                    @search="filterByProductName"
                />
            </template>

            <template #cell-productName="data">
                {{ getProductName(data.item) }}
            </template>

            <template #filter-cell-status>
                <VueSelectFilter
                    style="min-width: 110px;"
                    :options="statusOptions"
                    :initial-value="filters.status"
                    @filter="filterByStatus"
                />
            </template>

            <template #cell-status="data">
                <div @click.stop.prevent="onStatusClick(data.item)">
                    <StatusBadge
                        :class="{'text-decoration-underline': needToUnderlineStatus(data.item)}"
                        type="received"
                        :status="data.item.status"
                    />
                </div>
            </template>

            <template #cell-legislations="data">
                <div
                    class="received-requests-regulations"
                    :title="getRegulationsTitle(data.item)"
                >
                    <span
                        v-for="(regulation, index) in getVisibleRegulations(data.item)"
                        :key="`${data.item.id}-regulation-${index}`"
                        class="received-requests-regulation"
                    >
                        {{ regulation }}
                    </span>
                    <span
                        v-if="getHiddenRegulationsCount(data.item) > 0"
                        class="received-requests-regulation-more"
                    >
                        +{{ getHiddenRegulationsCount(data.item) }}
                    </span>
                </div>
            </template>

            <template #filter-cell-legislations="data">
                <LegislationsFilter
                    style="min-width: 130px;"
                    :initial-value="filters.legislations"
                    @selected="filterLegislations"
                />
            </template>

            <template #filter-cell-customer="data">
                <FilterInput
                    style="min-width: 120px;"
                    :initial-value="filters.customer"
                    @search="filterByCustomer"
                />
            </template>

            <template #cell-customer="data">
                {{ data.item.customer ? data.item.customer.title : '' }}
            </template>
        </HamletTable>
    </div>
</template>
<script>
import { BAlert, BCardText, BBadge, BTable, BCardTitle, BFormGroup, BButton, BCardGroup,
    BFormCheckbox, BIconEnvelopeOpen, BIconEnvelope, } from 'bootstrap-vue'
import {ProductApi, SupplyChain} from "@core/api";
import {getOurIdentifier, getOurName, RequestStatus} from "@/libs/product";
import StatusBadge from "@/views/request/components/StatusBadge";
import {ColumnsSelector, HamletTable} from "@core/components/common";
import {VueSelectFilter, FilterInput, AutoSuggestFilter, LegislationsFilter} from "@core/components/common/filter";
import dateRenderer from "@core/mixins/date-renderer";
import activeTableColumns from "@core/mixins/active-table-columns";
import ForwardedRequestsModal from "@/views/request/received/flow/components/vertiacal-steps/ForwardedRequestsModal.vue";
import {ProductBreadcrumbs, receivedRequestProvider} from "@core/domain/product";

const DEFAULT_PER_PAGE = 10;
const BULK_REPLY_SUPPORTED_FLOWS = new Set([
    'received-request-reply',
    'simple',
    'cellcomb',
]);

function normalizeReceivedRequestsSettings(settings) {
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
        return {
            filters: {},
            perPage: DEFAULT_PER_PAGE,
            page: 1,
        };
    }

    if (Object.prototype.hasOwnProperty.call(settings, 'filters')) {
        return {
            filters: structuredClone(settings.filters || {}),
            perPage: Number(settings.perPage) || DEFAULT_PER_PAGE,
            page: Number(settings.page) || 1,
        };
    }

    return {
        filters: structuredClone(settings),
        perPage: DEFAULT_PER_PAGE,
        page: 1,
    };
}

export default {
    mixins: [dateRenderer, activeTableColumns],
    components: {
        HamletTable, VueSelectFilter, FilterInput, AutoSuggestFilter, LegislationsFilter, ForwardedRequestsModal,
        BBadge, BCardText, BButton, BAlert, BFormGroup, BCardGroup, BTable, BCardTitle, StatusBadge,
        BFormCheckbox, BIconEnvelopeOpen, BIconEnvelope, ColumnsSelector,
    },
    data() {
        const savedSettings = normalizeReceivedRequestsSettings(
            this.$store.getters.runtimeConfig.receivedRequestsSettings
        );

        // {
        //     key: 'level', label: this.$t('Page(ReceivedRequests):Component(Grid):field-level'),
        //     title: this.$t('Page(ReceivedRequests):Component(Grid):hover-field-level'),
        //     active: false,
        // },
        return {
            filters: savedSettings.filters,
            savedPerPage: savedSettings.perPage,
            savedPage: savedSettings.page,
            isSearchActive: false,
            statusOptions: RequestStatus.all().map((status) => {
                return {
                    status: status,
                    title: this.$t('Component(Text):status-' + status)
                };
            }),
            selectionField: {
                key: 'selected',
                label: '',
                order: -1,
                active: true,
                thClass: 'received-requests-selection-column',
                tdClass: 'received-requests-selection-column',
            },
            fields: [
                {
                    key: 'messagingStatus',
                    label: this.$t('Page(SentRequests):Component(Grid):field-messaging-status'),
                    title: this.$t('Page(ReceivedRequests):Component(Grid):hover-field-messaging-status'),
                    order: 0,
                    active: true,
                },
                {
                    key: 'status', label: this.$t('Page(ReceivedRequests):Component(Grid):field-status'),
                    title: this.$t('Page(ReceivedRequests):Component(Grid):hover-field-status'),
                    order: 1,
                    active: true,
                },
                { key: 'sentAt', label: this.$t('Page(ReceivedRequests):Component(Grid):field-sent-at'), order: 2, active: true },
                { key: 'deadlineDays', label: this.$t('Page(ReceivedRequests):Component(Grid):field-deadline-days'), order: 3, active: true },
                {
                    key: 'productId', label: this.$t('Page(ReceivedRequests):Component(Grid):field-product-id'),
                    title: this.$t('Page(ReceivedRequests):Component(Grid):hover-field-product-id'),
                    order: 4,
                    active: true,
                },
                { key: 'productName', label: this.$t('Page(ReceivedRequests):Component(Grid):field-product-name'), order: 5, active: true },
                { key: 'customer', label: this.$t('Page(ReceivedRequests):Component(Grid):field-customer'), order: 6, active: true },
                {
                    key: 'legislations', label: this.$t('Page(ReceivedRequests):Component(Grid):field-regulations'),
                    title: this.$t('Page(ReceivedRequests):Component(Grid):hover-field-regulations'),
                    order: 7,
                    active: true,
                },
                {
                    key: 'verification', label: this.$t('Page(ReceivedRequests):Component(Grid):field-verification'),
                    title: this.$t('Page(ReceivedRequests):Component(Grid):hover-field-verification'),
                    order: 8,
                    active: false,
                },
            ],
            requests: [],
            statuses: RequestStatus,
            primaryBulkReplyRequest: null,
            bulkReplyCandidateIds: [],
            selectedBulkReplyRequestIds: [],
            bulkReplyCandidatesLoading: false,
            bulkReplyCandidatesLoaded: false,
            bulkReplyCandidatesError: null,
            bulkReplySelectionRevision: 0,
        };
    },
    methods: {
        getDefaultFilters() {
            const filters = structuredClone(this.filters);

            if (filters.status instanceof Array) {
                filters.status = filters.status.map(status => status.status);
            }

            return filters;
        },
        saveSettings() {
            this.$store.dispatch('setRuntimeProperty', {
                name: 'receivedRequestsSettings',
                value: {
                    filters: this.filters,
                    perPage: this.savedPerPage,
                    page: this.savedPage,
                },
                removeOnOrganizationSwitch: true,
            });
        },
        onPerPageChange(perPage) {
            this.savedPerPage = perPage;
            this.saveSettings();
        },
        onPageChange(page) {
            this.savedPage = page;
            this.saveSettings();
        },
        showColumns() {
            this.$refs['received-requests-columns'].show();
        },
        isBulkReplyRequestSelected(requestId) {
            return this.selectedBulkReplyRequestIds.includes(requestId);
        },
        isBulkReplyRequestAvailable(request) {
            return request.status === RequestStatus.NEW
                && BULK_REPLY_SUPPORTED_FLOWS.has(request.replyProcessFlow);
        },
        isBulkReplyRequestSelectable(request) {
            if (this.bulkReplyCandidatesLoading || !this.isBulkReplyRequestAvailable(request)) {
                return false;
            }

            if (!this.primaryBulkReplyRequest) {
                return true;
            }

            return request.id === this.primaryBulkReplyRequest.id
                || this.bulkReplyCandidateIds.includes(request.id);
        },
        toggleBulkReplyRequest(request, checked) {
            if (!this.isBulkReplyRequestSelectable(request)) {
                return;
            }

            if (!this.primaryBulkReplyRequest) {
                if (checked) {
                    this.selectPrimaryBulkReplyRequest(request);
                }
                return;
            }

            if (request.id === this.primaryBulkReplyRequest.id) {
                if (!checked) {
                    this.clearBulkReplySelection();
                }
                return;
            }

            if (checked && !this.isBulkReplyRequestSelected(request.id)) {
                this.selectedBulkReplyRequestIds = [
                    ...this.selectedBulkReplyRequestIds,
                    request.id,
                ];
            } else if (!checked) {
                this.selectedBulkReplyRequestIds = this.selectedBulkReplyRequestIds
                    .filter(requestId => requestId !== request.id);
            }
        },
        toggleAllBulkReplyCandidates(checked) {
            if (!this.canToggleAllBulkReplyCandidates) {
                return;
            }

            if (checked) {
                this.selectedBulkReplyRequestIds = Array.from(new Set([
                    ...this.selectedBulkReplyRequestIds,
                    ...this.currentPageBulkReplyCandidateIds,
                ]));
                return;
            }

            this.selectedBulkReplyRequestIds = this.selectedBulkReplyRequestIds
                .filter(requestId => !this.currentPageBulkReplyCandidateIds.includes(requestId));
        },
        selectPrimaryBulkReplyRequest(request) {
            const revision = ++this.bulkReplySelectionRevision;

            this.primaryBulkReplyRequest = request;
            this.selectedBulkReplyRequestIds = [request.id];
            this.bulkReplyCandidateIds = [];
            this.bulkReplyCandidatesLoaded = false;
            this.bulkReplyCandidatesError = null;
            this.bulkReplyCandidatesLoading = true;

            receivedRequestProvider(request.id)
                .getBulkReplyCandidates()
                .then(({ data }) => {
                    if (revision !== this.bulkReplySelectionRevision) {
                        return;
                    }

                    const candidates = Array.isArray(data.candidates) ? data.candidates : [];
                    this.bulkReplyCandidateIds = Array.from(new Set(candidates
                        .map(candidate => candidate.requestId)
                        .filter(requestId => requestId !== request.id)));
                    this.bulkReplyCandidatesLoaded = true;
                })
                .catch(error => {
                    if (revision !== this.bulkReplySelectionRevision) {
                        return;
                    }

                    this.bulkReplyCandidatesError = error;
                    this.bulkReplyCandidateIds = [];
                    this.bulkReplyCandidatesLoaded = false;
                })
                .finally(() => {
                    if (revision === this.bulkReplySelectionRevision) {
                        this.bulkReplyCandidatesLoading = false;
                    }
                });
        },
        retryBulkReplyCandidates() {
            if (this.primaryBulkReplyRequest) {
                this.selectPrimaryBulkReplyRequest(this.primaryBulkReplyRequest);
            }
        },
        clearBulkReplySelection() {
            this.bulkReplySelectionRevision += 1;
            this.primaryBulkReplyRequest = null;
            this.bulkReplyCandidateIds = [];
            this.selectedBulkReplyRequestIds = [];
            this.bulkReplyCandidatesLoading = false;
            this.bulkReplyCandidatesLoaded = false;
            this.bulkReplyCandidatesError = null;
        },
        startBulkReply() {
            if (!this.canStartBulkReply) {
                return;
            }

            const additionalRequestIds = this.selectedBulkReplyRequestIds
                .filter(requestId => requestId !== this.primaryBulkReplyRequest.id);

            this.$router.push({
                name: 'received-request',
                params: {
                    id: this.primaryBulkReplyRequest.id,
                    pid: this.primaryBulkReplyRequest.product.id,
                },
                query: {
                    additionalRequestIds: additionalRequestIds.join(','),
                },
            });
        },
        getStatusesToShowSentRequests() {
            return RequestStatus.all();
            return [
                RequestStatus.FORWARDED_TO_SUPPLIER,
                RequestStatus.REPLIED,
                RequestStatus.APPROVED,
                RequestStatus.PARTIALLY_APPROVED,
                RequestStatus.REJECTED,
            ];
        },
        needToUnderlineStatus(request) {
            return this.getStatusesToShowSentRequests().includes(request.status);
        },
        filterLegislations(legislations) {
            this.filters.legislations = legislations.map(l => l.id);
            this.$refs.hamletTable.filter('legislations', this.filters.legislations);
            this.saveSettings();
        },
        filterByCustomer(customerQueryString) {
            if (customerQueryString) {
                this.filters.customer = customerQueryString;
            } else {
                delete this.filters.customer;
            }
            this.$refs.hamletTable.filter('customer', customerQueryString);
            this.saveSettings();
        },
        filterByProductId(id) {
            if (id) {
                this.filters.productId = id;
            } else {
                delete this.filters.productId;
            }
            this.$refs.hamletTable.filter('productId', id);
            this.saveSettings();
        },
        filterByProductName(name) {
            if (name) {
                this.filters.productName = name;
            } else {
                delete this.filters.productName;
            }
            this.$refs.hamletTable.filter('productName', name);
            this.saveSettings();
        },
        filterByStatus(statuses) {
            if (statuses.length > 0) {
                this.filters.status = statuses;
            } else {
                delete this.filters.status;
            }
            this.$refs.hamletTable.filter('status', statuses.map(s => s.status));
            this.saveSettings();
        },
        searchCustomers(qs) {
            return SupplyChain.searchCustomers(qs);
        },
        getProductName(sentRequest) {
            return getOurName(sentRequest.product) || (sentRequest.product.names[0] ? sentRequest.product.names[0].value : '');
        },
        getProductId(sentRequest) {
            return getOurIdentifier(sentRequest.product) || (sentRequest.product.identifiers[0] ? sentRequest.product.identifiers[0].value : '');
        },
        getRegulations(receivedRequest) {
            return (receivedRequest.legislations || [])
                .map(legislation => legislation.title)
                .filter(Boolean);
        },
        getVisibleRegulations(receivedRequest, limit = 2) {
            return this.getRegulations(receivedRequest).slice(0, limit);
        },
        getHiddenRegulationsCount(receivedRequest, limit = 2) {
            return Math.max(this.getRegulations(receivedRequest).length - limit, 0);
        },
        getRegulationsTitle(receivedRequest) {
            return this.getRegulations(receivedRequest).join(', ');
        },
        onRowClick(sentRequest) {
            this.$router.push({
                name: 'received-request',
                params: {id: sentRequest.id, pid: sentRequest.product.id},
            });
        },
        onStatusClick(customerRequest) {
            if (this.getStatusesToShowSentRequests().includes(customerRequest.status)) {
                this.$refs.activeSentRequests.show(
                    receivedRequestProvider(customerRequest.id)
                        .getForwardedRequests()
                        .then(r => {
                            return r.data;
                        })
                );
            } else {
                this.onRowClick(customerRequest);
            }
        },
        fetch(config, cb) {
            ProductApi
                .getCustomerRequests(config.filters, {}, config.page, config.perPage)
                .then(r => {
                    if (false === this.isSearchActive && r.data.totalItems > this.numberOfItemsToShowFilter) {
                        this.isSearchActive = true;
                    }
                    this.requests = r.data.items;
                    cb(r.data.totalItems, r.data.items);
                });
        }
    },
    computed: {
        tableFields() {
            return [this.selectionField, ...this.activeFields];
        },
        canStartBulkReply() {
            return !this.bulkReplyCandidatesLoading
                && this.primaryBulkReplyRequest !== null
                && this.selectedBulkReplyRequestIds.length >= 1;
        },
        canToggleAllBulkReplyCandidates() {
            return !this.bulkReplyCandidatesLoading
                && this.bulkReplyCandidatesLoaded
                && this.primaryBulkReplyRequest !== null
                && this.currentPageBulkReplyCandidateIds.length > 0;
        },
        currentPageBulkReplyCandidateIds() {
            return this.requests
                .filter(request => this.isBulkReplyRequestAvailable(request))
                .map(request => request.id)
                .filter(requestId => this.bulkReplyCandidateIds.includes(requestId));
        },
        selectedBulkReplyCandidateCount() {
            return this.currentPageBulkReplyCandidateIds
                .filter(requestId => this.selectedBulkReplyRequestIds.includes(requestId))
                .length;
        },
        areAllBulkReplyCandidatesSelected() {
            return this.canToggleAllBulkReplyCandidates
                && this.selectedBulkReplyCandidateCount === this.currentPageBulkReplyCandidateIds.length;
        },
        isBulkReplyCandidateSelectionPartial() {
            return this.selectedBulkReplyCandidateCount > 0
                && !this.areAllBulkReplyCandidatesSelected;
        },
        numberOfItemsToShowFilter() {
            return 10;
        }
    },
    created() {
        this.initializeTableColumns(this.fields);
        ProductBreadcrumbs.clear();
        ProductBreadcrumbs.push(
            'Received requests',
            this.$t('Component(Sidebar):received-requests'),
            () => {
                this.$router.push({name: 'received-requests'});
            }
        );
    }
}
</script>
<style>
.verification-badge {
    margin-bottom: 6px;
    margin-right: 6px;
}

.received-requests-header {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    width: 100%;
}

.received-requests-header__start {
    justify-self: start;
}

.received-requests-header__end {
    display: flex;
    align-items: center;
    justify-self: end;
}
.received-requests-table table th,
.received-requests-table table td {
    padding: 0.72rem 1rem;
}
.received-requests-selection-column {
    width: 1%;
    min-width: 0;
    white-space: nowrap;
    text-align: center;
}
.received-requests-regulations {
    white-space: nowrap;
}
.received-requests-regulation + .received-requests-regulation::before {
    content: ', ';
}
.received-requests-regulation-more {
    margin-left: 0.5rem;
    color: #6c757d;
}
</style>
